import React, { useCallback, useEffect, useState } from 'react';
import { Redirect, useLocation, useParams } from 'react-router';

import OrganizationPage from './pages/organization-registration';

import { AnimatedContainer } from './style';
import AffiliateRegistrationEntryPoint from '../../entry-point/affiliate-registration';
import {
  GetUserByVerificationHashVariables,
  GetUserByVerificationHashResponse,
  GET_USER_BY_HASH,
} from '@plug/domains/organization/graphql/mutations/get-user-by-hash';
import { useQuery } from '@apollo/client';
import { useCreateAccountContext } from '@plug/contexts/CreateAccountContext';
import { useCreateOrganizationContext } from '@plug/contexts/CreateOrganizationContext';
import { useNavigationMenuContext } from '@plug/contexts/NavigationMenuContext';
import { useAuthContext } from '@plug/contexts/auth-context';
import { logoutUserSession } from '../../helpers/session';
import { GetTermsQuery, GET_TERMS } from '@plug/graphql/queries/get-terms';
import { EFIZI_HOST_NAME, OLIST_HOST_NAME } from '@plug/config/consts';

export enum Pages {
  INITIAL,
  SELLER,
  ORGANIZATION,
}

interface LocationParamsI {
  email?: string;
  phone?: string;
  username?: string;
  page?: Pages;
  inviteHash?: string;
  organizationName?: string;
  incompleteFlow?: boolean;
  role?: Roles;
  document?: string;
  organizationId?: string;
  customFields: { key: string; value: string }[];
  indicatedBy?: string;
  step?: CreateAccountContextISteps;
}

const SignUp = (): JSX.Element => {
  const location = useLocation<LocationParamsI>();
  const params = useParams<{ hash?: string }>();
  const searchParams = new URLSearchParams(window.location.search);

  const statePage = location.state?.page;
  const email = location.state?.email;
  const role = location.state?.role;
  const organizationNameState = location.state?.organizationName;
  const incompleteFlow = location.state?.incompleteFlow;
  const organizationId = searchParams.get('organizationId');
  const passTrough =
    EFIZI_HOST_NAME.includes(window.location.hostname) ||
    OLIST_HOST_NAME.includes(window.location.hostname) ||
    !!organizationId;

  const {
    addValues,
    values,
    incompleteFlow: incompleteFlowOrg,
    setIncompleteFlow,
  } = useCreateOrganizationContext();
  const {
    organizationName,
    setOrganizationName,
    role: roleValue,
    setRole,
    setStep,
    addValues: addAffiliateValues,
    setOrganizationId,
    addInitialValues,
    indicatedBy,
    setIndicatedBy,
  } = useCreateAccountContext();

  const { resetContext } = useNavigationMenuContext();
  const { resetContext: resetAuthContext } = useAuthContext();

  const [page, setPage] = useState<Pages>(statePage || Pages.INITIAL);

  const { data } = useQuery<GetUserByVerificationHashResponse, GetUserByVerificationHashVariables>(
    GET_USER_BY_HASH,
    {
      variables: {
        input: {
          hash: params.hash as string,
        },
      },
      skip: !params?.hash || !!statePage,
    },
  );

  const addAffiliateValuesMemoized = useCallback(addAffiliateValues, []);
  const setOrganizationIdMemoized = useCallback(setOrganizationId, []);

  useEffect(() => {
    resetContext();
    resetAuthContext();
    logoutUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (data?.getUserByVerificationHash?.email) {
      if (!values.email)
        addValues({
          email: data?.getUserByVerificationHash.email,
        });

      setPage(Pages.ORGANIZATION);
    }
  }, [addValues, data, values.email]);

  useEffect(() => {
    if (organizationNameState && !organizationName && Pages.SELLER) {
      setOrganizationName(organizationNameState);
    }
  }, [organizationName, organizationNameState, setOrganizationName]);

  useEffect(() => {
    if (!!location.state?.indicatedBy && !indicatedBy && Pages.SELLER) {
      setIndicatedBy(location.state.indicatedBy);
    }
  }, [indicatedBy, location.state, setIndicatedBy]);

  useEffect(() => {
    if (email && !values.email) {
      addValues({
        email,
      });
    }

    if (role && !roleValue) {
      setRole(role);
    }
  }, [email, values.email, addValues, role, roleValue, setRole]);

  useEffect(() => {
    if (incompleteFlow && !incompleteFlowOrg && statePage === Pages.ORGANIZATION) {
      setIncompleteFlow(incompleteFlow);
    }
  }, [email, incompleteFlow, incompleteFlowOrg, setIncompleteFlow, statePage, values.email]);

  useEffect(() => {
    if (passTrough && page !== Pages.SELLER) {
      if (organizationId) setOrganizationIdMemoized(organizationId);

      setPage(Pages.SELLER);
    }
  }, [organizationId, page, passTrough, setOrganizationIdMemoized]);

  useEffect(() => {
    const values = {
      name: location?.state?.username,
      email: location?.state?.email,
      phone: location?.state?.phone,
      document: location?.state?.document,
      ...(location.state?.customFields?.length && {
        customFields: Object.entries(location.state.customFields).reduce(
          (prev, [_, { key, value }]) => ({
            ...prev,
            [key]: value,
          }),
          {},
        ),
      }),
      ...(location.state?.step && {
        password: `Plugone${Math.floor(Math.random() * 90) + 10}`,
      }),
    };

    addAffiliateValuesMemoized(values);
    addInitialValues(values);

    setOrganizationIdMemoized(location?.state?.organizationId || organizationId);

    if (location?.state?.step) setStep(location.state.step);
  }, [
    addAffiliateValuesMemoized,
    addInitialValues,
    location,
    organizationId,
    setOrganizationIdMemoized,
    setStep,
  ]);

  const Component = (): JSX.Element => {
    if (page === Pages.INITIAL && (params.hash || passTrough)) return <></>;

    switch (page) {
      case Pages.SELLER:
        return (
          <AffiliateRegistrationEntryPoint
            email={email}
            organizationName={organizationName}
            incompleteFlow={incompleteFlow}
          />
        );

      case Pages.ORGANIZATION:
        return <OrganizationPage />;

      default:
        return <Redirect to="/login" />;
    }
  };

  return (
    <AnimatedContainer doingAnimation={false}>
      <Component />
    </AnimatedContainer>
  );
};

export default SignUp;
